// db-helpers.ts
import { db } from "./db";

/**
 * Generic CRUD operations for Dexie DB tables
 */

const iDbAddRecord = async <T>(tableName: string, data: T): Promise<number> => {
  try {
    const table = db.table(tableName);
    const id = await table.add(data);
    return id as number;
  } catch (error) {
    console.error(`Error adding record to ${tableName}:`, error);
    throw error;
  }
};

/**
 * Create - Add multiple records to a table (bulk insert)
 * @param tableName - Name of the table
 * @param dataArray - Array of data objects to insert
 * @returns The ID of the last inserted record
 */
const iDbBulkAddRecords = async <T>(
  tableName: string,
  dataArray: T[],
): Promise<number> => {
  try {
    const table = db.table(tableName);
    const lastKey = await table.bulkAdd(dataArray);
    return lastKey as number;
  } catch (error) {
    console.error(`Error bulk adding records to ${tableName}:`, error);
    throw error;
  }
};

/**
 * Read - Get all records from a table
 * @param tableName - Name of the table
 * @returns Array of all records
 */
const iDbGetAllRecords = async <T>(tableName: string): Promise<T[]> => {
  try {
    const table = db.table(tableName);
    const records = await table.toArray();
    return records as T[];
  } catch (error) {
    console.error(`Error getting all records from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Read - Get records with filtering
 * @param tableName - Name of the table
 * @param filterFn - Filter function to apply
 * @returns Array of filtered records
 */
const iDbGetFilteredRecords = async <T>(
  tableName: string,
  filterFn: (record: T) => boolean,
): Promise<T[]> => {
  try {
    const table = db.table(tableName);
    const records = await table.filter(filterFn).toArray();
    return records as T[];
  } catch (error) {
    console.error(`Error getting filtered records from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Read - Get records by a specific field value
 * @param tableName - Name of the table
 * @param field - Field name to query
 * @param value - Value to match
 * @returns Array of matching records
 */
const iDbGetRecordsByField = async <T>(
  tableName: string,
  field: string,
  value: any,
): Promise<T[]> => {
  try {
    const table = db.table(tableName);
    const records = await table.where(field).equals(value).toArray();
    return records as T[];
  } catch (error) {
    console.error(`Error getting records by field from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Update - Update a record by ID
 * @param tableName - Name of the table
 * @param id - Primary key of the record
 * @param changes - Partial object with fields to update
 * @returns Number of records updated (0 or 1)
 */
const iDbUpdateRecord = async <T>(
  tableName: string,
  id: number,
  changes: Partial<T>,
): Promise<number> => {
  try {
    const table = db.table(tableName);
    const updated = await table.update(id, changes);
    return updated;
  } catch (error) {
    console.error(`Error updating record in ${tableName}:`, error);
    throw error;
  }
};

/**
 * Update - Update records matching a condition
 * @param tableName - Name of the table
 * @param filterFn - Filter function to identify records to update
 * @param changes - Partial object with fields to update
 * @returns Number of records updated
 */
const iDbUpdateFilteredRecords = async <T>(
  tableName: string,
  filterFn: (record: T) => boolean,
  changes: Partial<T>,
): Promise<number> => {
  try {
    const table = db.table(tableName);
    const updated = await table.filter(filterFn).modify(changes);
    return updated;
  } catch (error) {
    console.error(`Error updating filtered records in ${tableName}:`, error);
    throw error;
  }
};

/**
 * Update or Insert - Put a record (update if exists, insert if not)
 * @param tableName - Name of the table
 * @param data - Data object with primary key
 * @returns The primary key of the record
 */
const iDbUpsertRecord = async <T>(
  tableName: string,
  data: T,
): Promise<number> => {
  try {
    const table = db.table(tableName);
    const id = await table.put(data);
    return id as number;
  } catch (error) {
    console.error(`Error upserting record in ${tableName}:`, error);
    throw error;
  }
};

/**
 * Delete - Delete a record by ID
 * @param tableName - Name of the table
 * @param id - Primary key of the record to delete
 */
const iDbDeleteRecord = async (
  tableName: string,
  id: number,
): Promise<void> => {
  try {
    const table = db.table(tableName);
    await table.delete(id);
  } catch (error) {
    console.error(`Error deleting record from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Delete - Delete multiple records by IDs
 * @param tableName - Name of the table
 * @param ids - Array of primary keys to delete
 */
const iDbBulkDeleteRecords = async (
  tableName: string,
  ids: number[],
): Promise<void> => {
  try {
    const table = db.table(tableName);
    await table.bulkDelete(ids);
  } catch (error) {
    console.error(`Error bulk deleting records from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Delete - Delete records matching a condition
 * @param tableName - Name of the table
 * @param filterFn - Filter function to identify records to delete
 * @returns Number of records deleted
 */
const iDbDeleteFilteredRecords = async <T>(
  tableName: string,
  filterFn: (record: T) => boolean,
): Promise<number> => {
  try {
    const table = db.table(tableName);
    const deleted = await table.filter(filterFn).delete();
    return deleted;
  } catch (error) {
    console.error(`Error deleting filtered records from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Delete - Clear all records from a table
 * @param tableName - Name of the table
 */
const iDbClearTable = async (tableName: string): Promise<void> => {
  try {
    const table = db.table(tableName);
    await table.clear();
  } catch (error) {
    console.error(`Error clearing table ${tableName}:`, error);
    throw error;
  }
};

/**
 * Advanced - Get records with sorting
 * @param tableName - Name of the table
 * @param sortField - Field name to sort by (must be indexed)
 * @param reverse - Sort in descending order if true
 * @returns Array of sorted records
 */
const iDbGetSortedRecords = async <T>(
  tableName: string,
  sortField: string,
  reverse: boolean = false,
): Promise<T[]> => {
  try {
    const table = db.table(tableName);
    const collection = reverse
      ? table.orderBy(sortField).reverse()
      : table.orderBy(sortField);
    const records = await collection.toArray();
    return records as T[];
  } catch (error) {
    console.error(`Error getting sorted records from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Advanced - Search records with multiple conditions (AND logic)
 * @param tableName - Name of the table
 * @param field - Indexed field to query
 * @param value - Value to match
 * @param additionalFilter - Additional filter function for complex conditions
 * @returns Array of matching records
 */
const iDbSearchRecords = async <T>(
  tableName: string,
  field: string,
  value: any,
  additionalFilter?: (record: T) => boolean,
): Promise<T[]> => {
  try {
    const table = db.table(tableName);
    const query = table.where(field).equals(value);

    if (additionalFilter) {
      const records = await query.filter(additionalFilter).toArray();
      return records as T[];
    }

    const records = await query.toArray();
    return records as T[];
  } catch (error) {
    console.error(`Error searching records in ${tableName}:`, error);
    throw error;
  }
};

/**
 * Read - Get a single record by its primary key
 * @param tableName - Name of the table
 * @param key - Primary key of the record
 * @returns The record or undefined if not found
 */
const iDbGetRecordByKey = async <T>(
  tableName: string,
  key: string | number,
): Promise<T | undefined> => {
  try {
    const table = db.table(tableName);
    const record = await table.get(key);
    return record as T | undefined;
  } catch (error) {
    console.error(`Error getting record by key from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Delete - Delete a record by its primary key (string or number)
 * @param tableName - Name of the table
 * @param key - Primary key of the record to delete
 */
const iDbDeleteRecordByKey = async (
  tableName: string,
  key: string | number,
): Promise<void> => {
  try {
    const table = db.table(tableName);
    await table.delete(key);
  } catch (error) {
    console.error(`Error deleting record by key from ${tableName}:`, error);
    throw error;
  }
};

/**
 * Utility - Check if a record exists by ID
 * @param tableName - Name of the table
 * @param id - Primary key of the record
 * @returns True if record exists, false otherwise
 */
const iDbRecordExists = async (
  tableName: string,
  id: number,
): Promise<boolean> => {
  try {
    const table = db.table(tableName);
    const record = await table.get(id);
    return record !== undefined;
  } catch (error) {
    console.error(`Error checking if record exists in ${tableName}:`, error);
    throw error;
  }
};

/**
 * Utility - Check if a record exists by field value
 * @param tableName - Name of the table
 * @param field - Field name to check
 * @param value - Value to match
 * @returns True if record exists, false otherwise
 */
const iDbRecordExistsByField = async (
  tableName: string,
  field: string,
  value: any,
): Promise<boolean> => {
  try {
    const table = db.table(tableName);
    const record = await table.where(field).equals(value).first();
    return record !== undefined;
  } catch (error) {
    console.error(
      `Error checking if record exists by field in ${tableName}:`,
      error,
    );
    throw error;
  }
};

export {
  iDbAddRecord,
  iDbBulkAddRecords,
  iDbGetAllRecords,
  iDbGetFilteredRecords,
  iDbGetRecordsByField,
  iDbGetRecordByKey,
  iDbUpdateRecord,
  iDbUpdateFilteredRecords,
  iDbUpsertRecord,
  iDbDeleteRecord,
  iDbDeleteRecordByKey,
  iDbBulkDeleteRecords,
  iDbDeleteFilteredRecords,
  iDbClearTable,
  iDbGetSortedRecords,
  iDbSearchRecords,
  iDbRecordExists,
  iDbRecordExistsByField,
};
